import type { ActivationState } from './ActivationClaims';
import type { DataMode } from './ActivationClaims';
import { resolveDataMode } from './DataModeResolver';
import { FeatureGate } from './FeatureGate';
import { ActivationCertificateVerifier } from './ActivationCertificateVerifier';
import { persistLicenceBinding } from '@application/licence/ActivationInstallGuard';
import { CLOUD_WEB_PLACEHOLDER_TENANT } from '@application/licence/CloudWebHost';
import { parseLicenceBinding } from './LicenceBinding';
import { SecureLicenceStore } from './SecureLicenceStore';
import { MODULE_DEFINITIONS } from '@application/licence/ModuleCatalog';
import { setActiveRoleProfiles } from '@application/access/ViewAccessPolicy';

/**
 * Modules fins accordés = liste signée par le serveur (plan + achats à la carte), après
 * expansion des agrégats. Le poste ne re-borne jamais au plafond du tier : droits livrés =
 * droits vendus.
 */
function entitledModulesFromClaims(featureCodes: string[] | undefined): string[] {
  const gate = FeatureGate.fromList(featureCodes);
  return MODULE_DEFINITIONS.filter((module) => gate.has(module.code)).map((module) => module.code);
}

/** Droits signés d'une session Cloud navigateur (champs d'un jeton `entitlements` vérifié). */
export interface CloudWebEntitlementClaims {
  sub: string;
  tenant_id: string;
  features: string[];
  tier: string;
  max_salaries: number;
  max_users: number;
  max_devices: number;
  role_views: Record<string, string[]>;
  iat: number;
  exp: number;
}

/** Restriction d'écriture imposée par le serveur ou l'absence prolongée de contrôle. */
export interface LicenceRestriction {
  kind: 'revoked' | 'suspended' | 'offline_too_long';
  message: string;
  since: string;
}

/** État licence courant : certificat, mode de données et feature gate. */
export class LicenceRuntime {
  private state: ActivationState = { valid: false, errorCode: 'NO_ACTIVATION', isReadOnly: true };
  private verifier = new ActivationCertificateVerifier();
  featureGate: FeatureGate = FeatureGate.fromList([]);
  dataMode: DataMode | null = null;
  private devBypass = false;
  private cloudWeb = false;
  private restriction: LicenceRestriction | null = null;
  /** Entreprise à laquelle s'applique la restriction (celle dont le certificat était chargé à ce moment-là). */
  private restrictionTenantId: string | null = null;
  private baseReadOnly = false;

  getRestriction(): LicenceRestriction | null {
    return this.restriction;
  }

  /**
   * Bascule en lecture seule (révocation, suspension, hors ligne prolongé) ou lève la restriction.
   * Seule l'écriture est bloquée : les données ne sont jamais supprimées.
   */
  setRestriction(restriction: LicenceRestriction | null): void {
    if (!this.restriction) this.baseReadOnly = this.state.isReadOnly;
    this.restriction = restriction;
    this.restrictionTenantId = restriction ? (this.state.claims?.tenant_id ?? null) : null;
    this.state = { ...this.state, isReadOnly: this.baseReadOnly || restriction !== null };
  }

  getState(): ActivationState {
    return this.state;
  }

  isReadOnly(): boolean {
    return this.state.isReadOnly;
  }

  withDevBypass(): LicenceRuntime {
    this.devBypass = true;
    this.state = {
      valid: true,
      isReadOnly: false,
      claims: {
        iss: 'Nexxus Conseil',
        aud: 'ga.nexxus.erp',
        sub: 'dev-bypass',
        jti: 'dev-bypass',
        activation_id: 'dev-bypass',
        customer_id: 'dev-bypass',
        tenant_id: 'demo-nexxus-tenant',
        product_code: 'ERP',
        device_fingerprint: 'browser-test',
        data_mode: 'LOCAL_OFFLINE',
        features: MODULE_DEFINITIONS.map((module) => module.code),
        tier: 'standard',
        max_salaries: 9999,
        max_users: 9999,
        max_devices: 99,
        iat: 0,
        nbf: 0,
        exp: 4102444800,
        renewal_window_days: 30,
      },
    };
    this.featureGate = FeatureGate.fromList(this.getEntitledModuleCodes());
    this.dataMode = 'LOCAL_OFFLINE';
    return this;
  }

  /**
   * Cloud SaaS navigateur AVANT réception de droits signés : aucun module, aucun plafond,
   * écriture bloquée. Les droits réels n'arrivent qu'avec un jeton `entitlements` signé
   * (voir `applyCloudWebEntitlements`) ; sans lui le navigateur reste fermé.
   */
  withCloudWebPending(tenantId = CLOUD_WEB_PLACEHOLDER_TENANT): LicenceRuntime {
    this.cloudWeb = true;
    this.state = {
      valid: true,
      isReadOnly: true,
      claims: {
        iss: 'Nexxus Conseil',
        aud: 'ga.nexxus.erp',
        sub: 'cloud-web',
        jti: 'cloud-web',
        activation_id: 'cloud-web',
        customer_id: 'cloud-web',
        tenant_id: tenantId,
        product_code: 'ERP',
        device_fingerprint: 'cloud-web',
        data_mode: 'REMOTE_MULTI_STATION',
        deployment_mode: 'cloud',
        features: [],
        tier: 'starter',
        max_salaries: 0,
        max_users: 0,
        max_devices: 0,
        iat: 0,
        nbf: 0,
        exp: 0,
        renewal_window_days: 30,
      },
    };
    this.baseReadOnly = true;
    this.featureGate = FeatureGate.fromList([]);
    this.dataMode = 'REMOTE_MULTI_STATION';
    return this;
  }

  /** Vrai tant que la session Cloud navigateur n'a reçu aucun droit signé. */
  isCloudWebPending(): boolean {
    return this.cloudWeb && this.state.claims?.sub === 'cloud-web';
  }

  /**
   * Applique les droits RÉELS d'une session Cloud navigateur, extraits d'un jeton `entitlements`
   * déjà vérifié (signature, émetteur, type, tenant). Modules = liste signée (plan + achats).
   */
  applyCloudWebEntitlements(claims: CloudWebEntitlementClaims): void {
    if (!this.cloudWeb || !this.state.claims) return;
    const current = this.state.claims;
    this.baseReadOnly = false;
    this.state = {
      valid: true,
      isReadOnly: this.restriction !== null,
      claims: {
        ...current,
        sub: claims.sub,
        tenant_id: claims.tenant_id,
        features: claims.features,
        tier: claims.tier as typeof current.tier,
        max_salaries: claims.max_salaries,
        max_users: claims.max_users,
        max_devices: claims.max_devices,
        role_views: claims.role_views,
        iat: claims.iat,
        nbf: claims.iat,
        exp: claims.exp,
        data_mode: 'REMOTE_MULTI_STATION',
        deployment_mode: 'cloud',
      },
    };
    this.featureGate = FeatureGate.fromList(this.getEntitledModuleCodes());
    this.dataMode = 'REMOTE_MULTI_STATION';
    setActiveRoleProfiles(claims.role_views);
  }

  /** Après connexion Cloud : rattache le dossier réel (plus le tenant provisoire). */
  bindCloudWebTenant(tenantId: string): void {
    if (!this.state.claims || !tenantId) return;
    this.state = {
      ...this.state,
      claims: { ...this.state.claims, tenant_id: tenantId },
    };
  }

  async loadFromStore(tenantId = '', salariesCount = 0): Promise<ActivationState> {
    if (this.devBypass || this.cloudWeb) return this.state;

    if (window.electron?.validateLocalLicence) {
      const result = await Promise.race([
        window.electron.validateLocalLicence(tenantId, salariesCount),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error('Délai dépassé : contrôle de licence')), 8_000);
        }),
      ]);
      this.applyState(result);
      return this.state;
    }

    const jwt = await SecureLicenceStore.getActivationCertificate();
    if (!jwt) {
      this.state = { valid: false, errorCode: 'NO_ACTIVATION', isReadOnly: true };
      return this.state;
    }

    const fingerprint = (await SecureLicenceStore.getDeviceFingerprint()) ?? 'browser-test';
    const state = await this.verifier.verifyCertificate(jwt, fingerprint, tenantId, salariesCount);
    this.applyState(state);
    return this.state;
  }

  async revalidateWithTenant(tenantId: string, salariesCount: number): Promise<ActivationState> {
    return this.loadFromStore(tenantId, salariesCount);
  }

  private applyState(next: ActivationState): void {
    // La révocation / suspension est celle d'UNE entreprise : si le certificat chargé appartient à une autre
    // entreprise (nouvelle activation), l'ancienne restriction ne s'applique plus. Le statut propre à la nouvelle
    // entreprise est relu au chargement (LicenceStatusService.refreshForActiveTenant / restore).
    const nextTenant = next.claims?.tenant_id;
    if (
      this.restriction &&
      this.restrictionTenantId &&
      nextTenant &&
      nextTenant !== this.restrictionTenantId
    ) {
      this.restriction = null;
      this.restrictionTenantId = null;
    }
    this.baseReadOnly = next.isReadOnly;
    const state = this.restriction ? { ...next, isReadOnly: true } : next;
    this.state = state;
    if (state.valid && state.claims) {
      void this.ensureBindingFromClaims(state.claims);
      this.featureGate = FeatureGate.fromList(this.getEntitledModuleCodes());
      this.dataMode = resolveDataMode(state.claims);
      setActiveRoleProfiles(state.claims.role_views);
    } else {
      this.featureGate = FeatureGate.fromList([]);
      this.dataMode = null;
      setActiveRoleProfiles(null);
    }
  }

  /** Met à jour modules et profils depuis le serveur sans renouveler le certificat. */
  applyEntitlements(
    features: string[],
    roleViews: Record<string, string[]>,
    tier?: string,
    dataMode?: DataMode,
    deploymentMode?: 'standalone' | 'hybride' | 'cloud',
  ): void {
    if (!this.state.claims) return;
    this.state = {
      ...this.state,
      claims: {
        ...this.state.claims,
        features,
        role_views: roleViews,
        ...(tier ? { tier: tier as typeof this.state.claims.tier } : {}),
        ...(dataMode ? { data_mode: dataMode } : {}),
        ...(deploymentMode ? { deployment_mode: deploymentMode } : {}),
      },
    };
    this.featureGate = FeatureGate.fromList(this.getEntitledModuleCodes());
    setActiveRoleProfiles(roleViews);
    if (dataMode) {
      this.dataMode = dataMode;
    }
  }

  /** Modules réellement accordés par le plan et les achats complémentaires. */
  getEntitledModuleCodes(): string[] {
    return entitledModulesFromClaims(this.state.claims?.features);
  }

  /** Modules actuellement activés dans l'interface de l'entreprise. */
  getEnabledModuleCodes(): string[] {
    return MODULE_DEFINITIONS.filter((module) => this.featureGate.has(module.code)).map(
      (module) => module.code,
    );
  }

  /**
   * Applique une préférence d'affichage sans jamais ouvrir un module non souscrit.
   * Les claims restent inchangées afin de conserver la preuve complète des droits achetés.
   */
  applyModuleSelection(requestedFeatures: string[]): string[] {
    const entitled = new Set(this.getEntitledModuleCodes());
    const requested = FeatureGate.fromList(requestedFeatures);
    const enabled = MODULE_DEFINITIONS.filter(
      (module) => entitled.has(module.code) && requested.has(module.code),
    ).map((module) => module.code);
    this.featureGate = FeatureGate.fromList(enabled);
    return enabled;
  }

  private async ensureBindingFromClaims(
    claims: NonNullable<ActivationState['claims']>,
  ): Promise<void> {
    if (this.devBypass) return;
    const existing = parseLicenceBinding(await SecureLicenceStore.getLicenceBinding());
    if (!existing) {
      await persistLicenceBinding(claims);
    }
  }
}
